import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import CotizadorError from '../components/CotizadorError';
import CotizadorHero from '../components/CotizadorHero';
import OffersSection from '../components/OffersSection';
import ProductsSidebar from '../components/ProductsSidebar';
import { getOffers } from '../services/offers.service';
import { getRequest } from '../services/request.service';
import '../styles/Cotizador.css';

const getProviderId = (provider) => {
  if (!provider) return null;
  if (typeof provider === 'string' || typeof provider === 'number') return provider;
  return provider._id ?? null;
};

const computeMissingProviders = (providers = [], offers = []) => {
  if (!providers?.length) return [];
  const offerProviderIds = new Set((offers || []).map((offer) => offer?.provider?._id ?? offer?.providerId).filter(Boolean));
  return providers.filter((provider) => {
    const providerId = getProviderId(provider);
    if (!providerId) return true;
    return !offerProviderIds.has(providerId);
  });
};

const getHighestMaxAmount = (offers = [], fallback = 0) => {
  if (!Array.isArray(offers) || !offers.length) {
    return fallback || 0;
  }

  return offers.reduce((max, offer) => {
    if (!offer || offer.status !== 'ok') {
      return max;
    }
    return Math.max(Number(offer.maxAmount) || 0, max);
  }, fallback || 0);
};

function Cotizador() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [request, setRequest] = useState(null);
  const [missingProviders, setMissingProviders] = useState([]);
  const { id } = useParams();
  const requestId = request?._id ?? request?.id ?? null;

  useEffect(() => {
    let canceled = false;

    const loadRequest = async () => {
      if (!id) {
        setError("No encontramos la solicitud seleccionada.");
        setRequest(null);
        return;
      }

      setIsLoading(true);
      setError('');

      const { success, data, error: serviceError } = await getRequest(id);
      if (canceled) return;

      if (!success) {
        setRequest(null);
        setMissingProviders([]);
        setError(serviceError || 'No pudimos cargar los datos del cotizador.');
        setIsLoading(false);
        return;
      }

      const currentOffers = Array.isArray(data?.offers) ? data.offers : [];
      const highestAmount = getHighestMaxAmount(currentOffers, data?.maxAmount || 0);
      setRequest({ ...data, offers: currentOffers, maxAmount: highestAmount });
      setMissingProviders(computeMissingProviders(data?.providers || [], currentOffers));
      setError('');
      setIsLoading(false);
    };

    loadRequest();
    return () => {
      canceled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!requestId || !missingProviders.length) {
      return undefined;
    }

    let canceled = false;
    let timeoutId = null;

    const retryLater = (fn, delayMs = 3000) => {
      timeoutId = setTimeout(() => {
        if (!canceled) {
          fn();
        }
      }, delayMs);
    };

    const loadOffers = async () => {
      const currentMissing = [...missingProviders];
      const { success, data, error: serviceError } = await getOffers(requestId, currentMissing);
      if (canceled) return;

      if (!success) {
        setError((prev) => prev || serviceError || 'No pudimos cargar todas las ofertas.');
        retryLater(loadOffers);
        return;
      }

      const newOffers = Array.isArray(data) ? data : data?.offers || [];
      if (newOffers.length) {
        setRequest((prev) => {
          if (!prev) return prev;
          const updatedOffers = [...prev.offers, ...newOffers];
          const updatedMaxAmount = getHighestMaxAmount(updatedOffers, prev.maxAmount || 0);
          const remaining = computeMissingProviders(prev.providers || [], updatedOffers);
          setMissingProviders(remaining);
          return { ...prev, offers: updatedOffers, maxAmount: updatedMaxAmount };
        });
      } else {
        // No new offers yet, retry later
        retryLater(loadOffers);
      }

    };

    loadOffers();

    return () => {
      canceled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [requestId, missingProviders]);

  const sortedOffers = useMemo(() => {
    if (!request?.offers?.length) return [];
    return [...request.offers].sort((a, b) => (b?.maxAmount || 0) - (a?.maxAmount || 0));
  }, [request]);

  const loadingMore = Boolean(request && missingProviders.length);

  const client = request?.client;
  const clientName = client ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() : '-';

  return (
    <div className="py-5" style={{background: 'var(--color-surface)', minHeight: 'calc(100vh - 120px)'}}>
      <Container fluid="lg">
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-3" style={{minHeight: '50vh'}}>
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mb-0">Cargando resultados...</p>
          </div>
        ) : request ? (
          <>
            <CotizadorHero request={request} clientName={clientName} clientDNI={client?.dni || '-'} />
            <Row className="g-4">
              <Col lg={8}>
                <OffersSection
                  offers={sortedOffers}
                  loadingMore={loadingMore}
                />
              </Col>
              <Col lg={4}>
                <ProductsSidebar maxAmount={request.maxAmount} />
              </Col>
            </Row>
          </>
        ) : (
          <CotizadorError error={error || 'No encontramos la solicitud seleccionada.'} />
        )}
      </Container>
    </div>
  );
}

export default Cotizador;
